import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Zap, IndianRupee, Calendar, Home, Building2, Users, Sparkles } from "lucide-react";
import ScToolkitBack from "../../components/sc/ScToolkitBack";

// Same India solar-sizing math used on the public /quote estimator — kept
// consistent so field numbers never contradict the marketing site.
function estimate({ monthlyBill, propertyType, subsidyCap }) {
  const tariff = 8; // ₹/unit average residential
  const monthlyUnits = Math.max(0, monthlyBill / tariff);
  const kwRaw = monthlyUnits / 120; // ~120 units/kW/month India avg
  const kw = Math.max(1, Math.round(kwRaw));
  const areaSqft = kw * 100;
  const systemCost = kw * 60000; // ₹60k/kW residential turnkey
  let subsidy = 0;
  if (propertyType === "residential") {
    if (kw >= 3) subsidy = subsidyCap || 78000;
    else if (kw === 2) subsidy = Math.min(60000, subsidyCap || 60000);
    else if (kw === 1) subsidy = Math.min(30000, subsidyCap || 30000);
  } else if (propertyType === "housing_society") {
    subsidy = Math.min(kw, 500) * 18000;
  }
  const netCost = Math.max(0, systemCost - subsidy);
  const maxGenSavings = kw * 120 * tariff; // generation-capped savings ceiling
  const monthlySavings = Math.round(Math.min(monthlyBill * 0.9, maxGenSavings));
  const annualSavings = monthlySavings * 12;
  const paybackYears = annualSavings > 0 ? netCost / annualSavings : 0;
  return { kw, areaSqft, systemCost, subsidy, netCost, monthlySavings, annualSavings, paybackYears };
}

const PROPERTY_TYPES = [
  { value: "residential", label: "Home", Icon: Home },
  { value: "commercial", label: "Business", Icon: Building2 },
  { value: "housing_society", label: "Society", Icon: Users },
];

const BILL_PRESETS = [1500, 3000, 5000, 8000, 15000];

function fmt(n) { return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }); }

export default function ScCalculator() {
  const [bill, setBill] = useState(5000);
  const [property, setProperty] = useState("residential");
  const [subsidyCap, setSubsidyCap] = useState(78000);

  useEffect(() => {
    api.get("/sc/flyer-data").then(({ data }) => {
      if (data?.subsidy_amount) setSubsidyCap(data.subsidy_amount);
    }).catch(() => {});
  }, []);

  const est = useMemo(() => estimate({ monthlyBill: Number(bill), propertyType: property, subsidyCap }), [bill, property, subsidyCap]);

  return (
    <div data-testid="sc-calculator" className="pb-6">
      <ScToolkitBack />
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Toolkit</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1 text-slate-900">Savings calculator.</h1>
      <p className="text-sm text-slate-500 mt-1.5">Enter the customer's monthly bill in front of them to show live system size, subsidy and payback.</p>

      {/* Property type */}
      <div className="mt-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Property type</div>
        <div className="grid grid-cols-3 gap-2.5" data-testid="calc-property-row">
          {PROPERTY_TYPES.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setProperty(p.value)}
              data-testid={`calc-property-${p.value}`}
              className={"py-3 text-center text-[10px] font-semibold uppercase tracking-widest rounded-xl transition-all active:scale-95 flex flex-col items-center gap-1.5 border " +
                (property === p.value
                  ? "bg-[#1B5E20] text-white border-[#1B5E20] shadow-sm"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-300")}
            >
              <p.Icon className="w-4 h-4" /> {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bill slider */}
      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monthly electricity bill</div>
          <div className="font-mono text-xl font-bold text-[#1B5E20]" data-testid="calc-bill-value">₹{fmt(bill)}</div>
        </div>
        <input
          type="range"
          min="500"
          max="50000"
          step="500"
          value={bill}
          onChange={(e) => setBill(Number(e.target.value))}
          data-testid="calc-bill-slider"
          className="w-full mt-3 accent-[#1B5E20]"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {BILL_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setBill(p)}
              data-testid={`calc-preset-${p}`}
              className={"text-[10px] font-mono px-2.5 py-1 rounded-full border " +
                (bill === p ? "bg-[#1B5E20] text-white border-[#1B5E20]" : "border-slate-200 text-slate-600 hover:border-slate-300")}
            >
              ₹{fmt(p)}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="mt-5 bg-gradient-to-br from-[#1B5E20] to-[#134A18] p-5 rounded-2xl shadow-[0_4px_20px_rgba(27,94,32,0.25)] relative overflow-hidden" data-testid="calc-results">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-200 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-amber-300" /> Estimate for your customer
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">System size</div>
            <div className="font-mono text-4xl font-black text-white mt-1 leading-none" data-testid="calc-kw">
              {est.kw}<span className="text-lg text-emerald-100 ml-1">kW</span>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">Roof needed</div>
            <div className="font-mono text-2xl font-bold text-white mt-1 leading-none">
              {fmt(est.areaSqft)}<span className="text-xs text-emerald-100/80 ml-1">sqft</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-white/15">
          <div>
            <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">System cost</div>
            <div className="font-mono text-lg font-bold text-white mt-1">₹{fmt(est.systemCost)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">Govt. subsidy</div>
            <div className="font-mono text-lg font-bold text-amber-300 mt-1">−₹{fmt(est.subsidy)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">Net cost</div>
            <div className="font-mono text-lg font-bold text-white mt-1">₹{fmt(est.netCost)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">Payback</div>
            <div className="font-mono text-lg font-bold text-white mt-1">{est.paybackYears.toFixed(1)}<span className="text-xs ml-1">yrs</span></div>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-white/15 text-center">
          <div className="font-mono text-[10px] text-emerald-100/80 uppercase tracking-widest">Saves every month</div>
          <div className="font-mono text-4xl font-black text-amber-300 mt-1" data-testid="calc-monthly-savings">₹{fmt(est.monthlySavings)}</div>
          <div className="font-mono text-xs text-emerald-100/80 mt-1">≈ ₹{fmt(est.annualSavings)} per year</div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400 justify-center">
        <IndianRupee className="w-3 h-3" /> Indicative numbers · Final design confirmed after free site survey
      </div>
    </div>
  );
}
